import { createContext, useState, useEffect, ReactNode } from "react";
import { AuthState } from "@/domain/models";
import { useTranslation } from "react-i18next";
import { authService } from "@/domain/services/authService";
import { contextService } from "@/domain/services/contextService";
import { authRepository } from "@/infrastructure/authentication";

/**
 * 認證上下文介面
 */
interface AuthContextType extends AuthState {
  // login: (email: string, password: string) => Promise<void>;
  oauthWithGoogle: (response: object) => Promise<void>;
  loginWithGoogle: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * 認證上下文
 * 提供了整個應用程序的認證狀態和方法
 */
export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  error: null,
  // login: async () => {},
  oauthWithGoogle: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 認證提供者組件
 * 管理認證狀態並提供認證方法
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: authRepository.hasValidSession(),
    user: null,
    token: authRepository.retrieveToken(),
    loading: true,
    error: null,
  });

  const { t } = useTranslation();

  // 初始化時檢查用戶是否已經登入
  useEffect(() => {
    const initAuth = async () => {
      if (authState.isAuthenticated) {
        try {
          const user = await authService.getCurrentUser();
          setAuthState((prev) => ({
            ...prev,
            user,
            loading: false,
          }));
        } catch (error) {
          console.error("Failed to get current user:", error);
          // 如果獲取用戶信息失敗，清除認證狀態
          setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
            error: t("auth.login_failed"),
          });
          authRepository.terminateSession();
        }
      } else {
        setAuthState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };

    initAuth();
  }, []);

  /**
   * 使用 Google 登入
   * @param token Google 認證令牌
   */
  const loginWithGoogle = async (token: string) => {
    setAuthState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      // 首先使用 Google 令牌登入
      const { token: authToken } = await authService.loginWithGoogle(token);

      // 登入成功後，立即獲取最新的用戶資料
      try {
        // 使用安全 Cookie 儲存身份驗證資料
        authRepository.saveToken(authToken);

        // 獲取用戶上下文並保存到本地儲存
        const currentUser = await authService.getCurrentUser();
        await contextService.getCurrentContext();

        setAuthState({
          isAuthenticated: true,
          user: currentUser,
          token: authToken,
          loading: false,
          error: null,
        });
      } catch (userError) {
        console.error(
          "Failed to get current user after Google login:",
          userError,
        );
        // 即使獲取用戶資料失敗，仍然認為登入成功，但用戶資料可能不完整
        setAuthState({
          isAuthenticated: true,
          user: null, // 用戶資料空，後續可能需要重新獲取
          token: authToken,
          loading: false,
          error: t("auth.user_info_failed"),
        });
      }
    } catch (error) {
      console.error("Google login error:", error);
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        loading: false,
        error: t("auth.login_failed"),
      }));
    }
  };

  const oauthWithGoogle = async (response: object) => {
    setAuthState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      // 首先使用 Google 令牌登入
      const { token: authToken } = await authService.oauthWithGoogle(response);

      // 登入成功後，立即獲取最新的用戶資料
      try {
        // 使用安全 Cookie 儲存身份驗證資料
        authRepository.saveToken(authToken);

        // 獲取用戶上下文並保存到本地儲存
        const currentUser = await authService.getCurrentUser();
        await contextService.getCurrentContext();

        setAuthState({
          isAuthenticated: true,
          user: currentUser,
          token: authToken,
          loading: false,
          error: null,
        });
      } catch (userError) {
        console.error(
          "Failed to get current user after Google login:",
          userError,
        );
        // 即使獲取用戶資料失敗，仍然認為登入成功，但用戶資料可能不完整
        setAuthState({
          isAuthenticated: true,
          user: null, // 用戶資料空，後續可能需要重新獲取
          token: authToken,
          loading: false,
          error: t("auth.user_info_failed"),
        });
      }
    } catch (error) {
      console.error("Google login error:", error);
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        loading: false,
        error: t("auth.login_failed"),
      }));
    }
  };

  /**
   * 用戶登出
   */
  const logout = async () => {
    setAuthState((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      await authService.logout();

      // 使用安全 Cookie 清除身份驗證資料
      authRepository.terminateSession();
      contextService.saveLocalContext({});

      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Logout error:", error);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        // login,
        loginWithGoogle,
        oauthWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
